import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import useReveal from "../hooks/useReveal";
import { useT } from "../i18n/LanguageContext";
import FrameworkDiagram from "../components/FrameworkDiagram";
import BookCard from "../components/BookCard";
import WaitlistForm from "../components/WaitlistForm";

export default function Home() {
  const t = useT();
  const root = useReveal();
  const marquee = t("home.marquee");
  const problemCards = t("home.problem.cards");
  const volumes = t("home.curriculum.volumes");
  const methodSteps = t("home.method.steps");
  const creatorRoles = t("home.creator.roles");

  const VOL_PROPS = [
    { image: "/images/cover_body.png", word: "BODY" },
    { word: "MIND" },
    { word: "SPIRIT" },
  ];

  return (
    <div ref={root}>
      {/* HERO */}
      <section
        className="relative min-h-[92vh] flex items-center overflow-hidden pt-32 md:pt-40 pb-20"
        data-testid="home-hero"
      >
        <div className="container-x relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
            <div className="lg:col-span-7">
              <p className="reveal text-[0.7rem] uppercase tracking-[0.42em] text-[#9A7E47]">
                {t("home.hero.eyebrow")}
              </p>
              <h1 className="reveal reveal-delay-1 mt-8 font-serif text-[#1A1A1A] tracking-tight leading-[0.95] text-balance text-[3.4rem] sm:text-[4.5rem] md:text-[5.5rem] lg:text-[6rem] xl:text-[7rem]">
                {t("home.hero.title")}
              </h1>
              <div className="reveal reveal-delay-2 mt-12 max-w-xl space-y-4 text-lg text-[#4A4A4A] leading-relaxed">
                <p>{t("home.hero.intro")}</p>
                <p className="text-[#1A1A1A] font-medium">{t("home.hero.intro2")}</p>
              </div>
              <div className="reveal reveal-delay-3 mt-12 flex flex-wrap items-center gap-4">
                <Link to="/curriculum" className="btn-ink" data-testid="hero-cta-curriculum">
                  {t("home.hero.ctaPrimary")} <ArrowRight size={16} />
                </Link>
                <Link to="/method" className="btn-ghost" data-testid="hero-cta-method">
                  {t("home.hero.ctaSecondary")}
                </Link>
              </div>
              <p className="reveal reveal-delay-4 mt-14 text-[0.7rem] uppercase tracking-[0.32em] text-[#9A7E47]">
                <span className="tri inline-block mr-2 align-middle" />
                {t("home.hero.meta")}
              </p>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="reveal reveal-delay-2 relative max-w-md mx-auto">
                <img
                  src="/images/cover_body.png"
                  alt="Sanity · User Manual for the Body"
                  className="w-full h-auto book elev-1"
                />
                <p className="mt-6 text-center text-[0.65rem] uppercase tracking-[0.32em] text-[#9A7E47]">
                  Volume I · Releasing first
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-[rgba(26,26,26,0.06)] py-7 overflow-hidden bg-[#F3F1EA]">
        <div className="marquee-track flex items-center gap-16 whitespace-nowrap">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center gap-16 shrink-0">
              {marquee.map((w, i) => (
                <span
                  key={`${k}-${i}-${w}`}
                  className="font-serif italic text-3xl md:text-5xl text-[rgba(26,26,26,0.12)]"
                >
                  {w}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="container-x py-32 md:py-40" data-testid="home-problem">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
          <div className="md:col-span-5">
            <p className="reveal eyebrow">{t("home.problem.eyebrow")}</p>
            <h2 className="reveal reveal-delay-1 mt-6 font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#1A1A1A] leading-[1.05]">
              {t("home.problem.title")}
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-6">
            <p className="reveal reveal-delay-2 text-lg md:text-xl text-[#4A4A4A] leading-relaxed">
              {t("home.problem.intro")}
            </p>
          </div>
        </div>

        <div className="hairline mt-20" />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[rgba(26,26,26,0.06)] mt-20 border border-[rgba(26,26,26,0.06)]">
          {problemCards.map((c, i) => (
            <div
              key={i}
              className={`reveal reveal-delay-${i + 1} bg-[#F8F7F2] p-10 md:p-12`}
              data-testid={`problem-card-${["body", "mind", "spirit"][i]}`}
            >
              <p className="text-[0.65rem] uppercase tracking-[0.32em] text-[#9A7E47]">
                {c.tag}
              </p>
              <h3 className="mt-6 font-serif text-4xl md:text-5xl text-[#1A1A1A] tracking-tight">
                {c.title}
              </h3>
              <div className="hairline-gold mt-6 w-12" />
              <p className="mt-6 text-[#4A4A4A] leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FRAMEWORK */}
      <section
        className="bg-[#F3F1EA] border-y border-[rgba(26,26,26,0.06)] py-32 md:py-40 grain relative overflow-hidden"
        data-testid="home-framework"
      >
        <div className="container-x relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-5">
              <p className="reveal eyebrow">{t("home.framework.eyebrow")}</p>
              <h2 className="reveal reveal-delay-1 mt-6 font-serif text-4xl md:text-6xl tracking-tight text-[#1A1A1A] leading-[1.02]">
                {t("home.framework.title")}
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 md:pt-6">
              <p className="reveal reveal-delay-2 text-lg text-[#4A4A4A] leading-relaxed">
                {t("home.framework.intro")}
              </p>
            </div>
          </div>
          <div className="reveal reveal-delay-2 mt-12">
            <FrameworkDiagram />
          </div>
        </div>
      </section>

      {/* CURRICULUM */}
      <section
        className="container-x py-32 md:py-40"
        data-testid="home-curriculum"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-20 items-end">
          <div className="md:col-span-7">
            <p className="reveal eyebrow">{t("home.curriculum.eyebrow")}</p>
            <h2 className="reveal reveal-delay-1 mt-6 font-serif text-4xl md:text-6xl tracking-tight text-[#1A1A1A] leading-[1.02]">
              {t("home.curriculum.title")}
            </h2>
            <p className="reveal reveal-delay-2 mt-6 max-w-xl text-[#4A4A4A] text-lg leading-relaxed">
              {t("home.curriculum.intro")}
            </p>
          </div>
          <div className="md:col-span-5 md:text-right">
            <Link
              to="/curriculum"
              className="gold-link"
              data-testid="home-curriculum-link"
            >
              {t("home.curriculum.ctaLink")} <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-14">
          {volumes.map((v, i) => (
            <BookCard
              key={i}
              num={v.num}
              title={v.title}
              sub={v.sub}
              word={VOL_PROPS[i].word}
              image={VOL_PROPS[i].image}
              testid={`home-volume-${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* METHOD */}
      <section className="bg-[#F3F1EA] border-y border-[rgba(26,26,26,0.06)] py-32 md:py-40">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-5">
              <p className="reveal eyebrow">{t("home.method.eyebrow")}</p>
              <h2 className="reveal reveal-delay-1 mt-6 font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#1A1A1A] leading-[1.02]">
                {t("home.method.title")}
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 md:pt-6">
              <p className="reveal reveal-delay-2 text-lg text-[#4A4A4A] leading-relaxed">
                {t("home.method.intro")}
              </p>
            </div>
          </div>

          <ol className="mt-16 grid grid-cols-1 md:grid-cols-5 gap-px bg-[rgba(26,26,26,0.06)] border border-[rgba(26,26,26,0.06)]">
            {methodSteps.map((s, i) => (
              <li
                key={i}
                className={`reveal reveal-delay-${(i % 5) + 1} bg-[#F8F7F2] p-8 md:p-10`}
                data-testid={`home-method-step-${i + 1}`}
              >
                <p className="font-serif italic text-[#9A7E47] text-3xl">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-5 font-serif text-xl md:text-2xl text-[#1A1A1A] tracking-tight">
                  {s.label}
                </h3>
                <p className="mt-4 text-[#4A4A4A] text-sm leading-relaxed">
                  {s.body}
                </p>
              </li>
            ))}
          </ol>

          <div className="mt-14 text-center">
            <Link to="/method" className="btn-ghost" data-testid="home-method-link">
              Learn the Method <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* CREATOR */}
      <section
        className="container-x py-32 md:py-40"
        data-testid="home-creator"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5">
            <div className="reveal relative aspect-[4/5] overflow-hidden surface elev-1">
              <img
                src="/images/eva_hero.png"
                alt="Eva Petkoff"
                className="absolute inset-0 w-full h-full object-cover img-tint"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="reveal eyebrow">{t("home.creator.eyebrow")}</p>
            <h2 className="reveal reveal-delay-1 mt-6 font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#1A1A1A] leading-[1.02]">
              {t("home.creator.name")}
            </h2>
            <ul className="reveal reveal-delay-2 mt-8 space-y-2 text-[#4A4A4A]">
              {creatorRoles.map((r, i) => (
                <li key={i} className="text-base">
                  <span className="tri inline-block mr-3" />
                  {r}
                </li>
              ))}
            </ul>
            <p className="reveal reveal-delay-3 mt-10 text-lg text-[#4A4A4A] leading-relaxed max-w-2xl">
              {t("home.creator.body")}
            </p>
            <div className="reveal reveal-delay-4 mt-10">
              <Link to="/about" className="gold-link" data-testid="home-creator-link">
                {t("home.creator.cta")} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        className="bg-[#1A1A1A] py-32 md:py-40 text-center grain"
        data-testid="home-final-cta"
      >
        <div className="container-x relative">
          <p className="reveal text-[0.7rem] uppercase tracking-[0.42em] text-[#D4BB85]">
            Sanity
          </p>
          <h2 className="reveal reveal-delay-1 mt-8 font-serif text-4xl md:text-6xl lg:text-7xl tracking-tight text-[#F8F7F2] leading-[1.02] max-w-4xl mx-auto text-balance">
            {t("home.finalCta.title")}
          </h2>
          <p className="reveal reveal-delay-2 mt-8 text-lg text-[#D4BB85] max-w-xl mx-auto">
            {t("home.finalCta.sub")}
          </p>
          <div className="reveal reveal-delay-3 mt-12">
            <Link
              to="/curriculum"
              className="inline-flex items-center gap-3 px-8 py-4 text-xs uppercase tracking-[0.18em] font-semibold border border-[#B89B5E] bg-[#B89B5E] text-[#1A1A1A] hover:bg-transparent hover:text-[#B89B5E] transition-colors duration-500"
              data-testid="home-final-cta-button"
            >
              {t("home.finalCta.button")} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* WAITLIST */}
      <section className="container-x py-32 md:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-5">
            <p className="reveal eyebrow">Enrol</p>
            <h3 className="reveal reveal-delay-1 mt-6 font-serif text-4xl md:text-5xl tracking-tight text-[#1A1A1A] leading-[1.02]">
              Receive each Volume as it opens.
            </h3>
            <p className="reveal reveal-delay-2 mt-6 text-[#4A4A4A] max-w-md leading-relaxed">
              Volume I — User Manual for the Body — releases first. Join the
              list to be among the first to receive it.
            </p>
          </div>
          <div className="lg:col-span-7">
            <WaitlistForm interest="curriculum" testIdPrefix="home-waitlist" />
          </div>
        </div>
      </section>
    </div>
  );
}
